import logo from '../../../assets/images/logo.png'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { LoadingButton } from '@mui/lab'
import { Alert, Snackbar, Stack } from '@mui/material'
import { FormProvider, useForm } from 'react-hook-form'
import TextInputField from '../../../components/fields/text-input-field/text-input-field.component'
import { useState, useEffect } from 'react'
import useSubmitLock from 'hooks/use-submit-lock'
import { useSetDispatchUserPasswordMutation } from 'store/dispatch-auth/dispatch-auth.api'

type FormValues = {
  password: string
  confirmPassword: string
}

const DispatchSetPasswordPage = () => {
  const [searchParams] = useSearchParams()
  const dispatchUserId = searchParams.get('dispatchUserId')

  const navigate = useNavigate()
  const formMethods = useForm<FormValues>({
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  })

  const [setPassword, { isLoading }] = useSetDispatchUserPasswordMutation()
  const { isSubmitting, wrapFormSubmit } = useSubmitLock()

  const [error, setError] = useState<string>('')
  const [errorOpen, setErrorOpen] = useState(false)
  const [success, setSuccess] = useState(false)

  useEffect(() => {
    if (!dispatchUserId) {
      setError('Invalid link. Please contact your administrator.')
      setErrorOpen(true)
    }
  }, [dispatchUserId])

  const onSubmit = async (data: FormValues) => {
    if (!dispatchUserId) {
      setError('Invalid link. Please contact your administrator.')
      setErrorOpen(true)
      return
    }

    if (data.password !== data.confirmPassword) {
      setError('Passwords do not match')
      setErrorOpen(true)
      return
    }

    try {
      await setPassword({ dispatchUserId, password: data.password }).unwrap()
      setSuccess(true)
      setTimeout(() => {
        navigate('/auth/login')
      }, 1500)
    } catch (e: any) {
      const message =
        e?.data?.error || e?.error || 'Failed to set password. Please try again.'
      setError(message)
      setErrorOpen(true)
    }
  }

  if (!dispatchUserId) {
    return (
      <div className="login-page">
        <div className="login-container">
          <h1>Create Password</h1>
          <img src={logo} alt="Transpots Logo" className="logo" />
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <p>Invalid link. Please contact your administrator.</p>
            <Link to="/auth/login" style={{ color: '#0075ff', textDecoration: 'none' }}>
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="login-page">
      <div className="login-container">
        <h1>Create Password</h1>
        <img src={logo} alt="Transpots Logo" className="logo" />

        {success ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Alert severity="success" sx={{ mb: 2 }}>
              Password created successfully! Redirecting to login...
            </Alert>
            <Link to="/auth/login" style={{ color: '#0075ff', textDecoration: 'none' }}>
              Go to Login
            </Link>
          </div>
        ) : (
          <FormProvider {...formMethods}>
            <form onSubmit={wrapFormSubmit(formMethods.handleSubmit, onSubmit)}>
              <Stack spacing={2} sx={{ width: '320px' }}>
                <TextInputField
                  name="password"
                  placeholder="Password"
                  type="password"
                  variant="standard"
                />
                <TextInputField
                  name="confirmPassword"
                  placeholder="Confirm Password"
                  type="password"
                  variant="standard"
                />
                <LoadingButton
                  variant="contained"
                  fullWidth
                  loading={isLoading || isSubmitting}
                  disabled={isLoading || isSubmitting}
                  sx={{
                    borderRadius: '4px',
                    '&.Mui-disabled': {
                      bgcolor: 'action.disabledBackground',
                      color: 'action.disabled',
                    },
                  }}
                  type="submit"
                >
                  CREATE PASSWORD
                </LoadingButton>
              </Stack>
            </form>
          </FormProvider>
        )}

        <div className="form-footer">
          <Link to="/auth/login">Back to Login</Link>
        </div>
      </div>

      <Snackbar
        open={errorOpen}
        autoHideDuration={6000}
        onClose={() => setErrorOpen(false)}
      >
        <Alert severity="error">{error}</Alert>
      </Snackbar>
    </div>
  )
}

export default DispatchSetPasswordPage
